import { ChangeEvent, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import Money from '@engineering/icons/money';
import { Ui } from '@silvertours/front-shared';
import { Runtime } from '@silvertours/front-entities';
import { FIELD_NAMES } from './constants';
import { Section } from './Section';

type Props = {
  /** Called after a currency has been selected. */
  onCurrencyChange?: (code: string) => void;
};

/**
 * Currency selection inside the regional settings. Unlike the market it is not a
 * redirect: changing the currency is applied immediately and client-side
 * (prices are re-converted via exchange rates through `usePrice`).
 */
const CurrencyField = ({ onCurrencyChange }: Props) => {
  const t = useTranslations(
    'features.navigationLegacy.navigation.header.regionalSettings',
  );
  const { locale } = Runtime.useLocale();
  const { selectedCurrency, setSelectedCurrency, currencies } =
    Runtime.useSelectedCurrency();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const { value } = event.target;
      setSelectedCurrency(value);
      onCurrencyChange?.(value);
    },
    [setSelectedCurrency, onCurrencyChange],
  );

  if (!currencies?.length) {
    return null;
  }

  return (
    <Section
      icon={<Money />}
      label={t('currency.label')}
      description={t('currency.description')}
    >
      <Ui.Dropdown
        id={FIELD_NAMES.CURRENCY}
        label={t('currency.label')}
        hideLabel
        value={selectedCurrency}
        onChange={handleChange}
        options={currencies.map(({ code }) => ({
          label: Runtime.getCurrencyName(code, locale),
          value: code,
        }))}
      />
    </Section>
  );
};

export { CurrencyField };
